import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { authApi, marketApi, portfolioApi, settingsApi } from "@/lib/api";
import { TID } from "@/lib/testIds";
import Watchlist from "@/components/dashboard/Watchlist";
import PriceChart from "@/components/dashboard/PriceChart";
import AISignalPanel from "@/components/dashboard/AISignalPanel";
import TradeExecutionPanel from "@/components/dashboard/TradeExecutionPanel";
import PortfolioSummary from "@/components/dashboard/PortfolioSummary";
import AutoTraderSettings from "@/components/dashboard/AutoTraderSettings";
import TradeHistory from "@/components/dashboard/TradeHistory";
import AlpacaConnect from "@/components/dashboard/AlpacaConnect";
import FeeStatus from "@/components/dashboard/FeeStatus";
import FeeReminderBanner from "@/components/dashboard/FeeReminderBanner";
import ReferralCard from "@/components/dashboard/ReferralCard";
import AdminPanel from "@/components/dashboard/AdminPanel";
import ProfitAnalytics from "@/components/dashboard/ProfitAnalytics";
import BotHealthPill from "@/components/dashboard/BotHealthPill";
import { FounderChip } from "@/components/FounderWelcome";
import TradingChartBackground from "@/components/TradingChartBackground";
import Chatbot from "@/components/Chatbot";
import { feesApi } from "@/lib/api";
import { RiskBanner, RiskFooter, RiskDisclaimerModal, useRiskAck } from "@/components/RiskDisclaimer";
import { LogOut, Play, Pause, Radar } from "lucide-react";

export default function Dashboard({ user: initialUser }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser || null);
  const [selected, setSelected] = useState("AAPL");
  const [watchlist, setWatchlist] = useState({ symbols: [], quotes: [] });
  const [portfolio, setPortfolio] = useState(null);
  const [settings, setSettings] = useState(null);
  const [trades, setTrades] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [feeSummary, setFeeSummary] = useState(null);
  const [feeBannerDismissed, setFeeBannerDismissed] = useState(false);
  const [chartEvents, setChartEvents] = useState([]);
  const risk = useRiskAck();

  // Convert new SELL trades with realized_pnl into background chart pop-ins.
  useEffect(() => {
    if (!trades || !trades.length) return;
    const relevant = trades
      .filter((t) => t.side === "SELL" && typeof t.realized_pnl === "number")
      .slice(0, 3)
      .map((t) => ({ id: t.trade_id, symbol: t.symbol, side: t.side, pnl: t.realized_pnl }));
    setChartEvents(relevant);
  }, [trades]);

  const goToStripeSettle = async () => {
    try {
      const { checkout_url } = await feesApi.checkout();
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe checkout");
    }
  };

  const refreshAll = async () => {
    const [wl, port, s, tr] = await Promise.all([
      marketApi.watchlist(),
      portfolioApi.get(),
      settingsApi.get(),
      portfolioApi.trades(),
    ]);
    setWatchlist(wl);
    setPortfolio(port);
    setSettings(s);
    setTrades(tr.trades || []);
  };

  useEffect(() => {
    // Bootstrap-once on mount. Adding refreshAll / marketApi / navigate to deps
    // would re-run this effect on every state change and thrash the API.
    if (!user) {
      authApi.me().then(setUser).catch((err) => {
        console.debug("[Dashboard] not authenticated:", err?.response?.status);
        navigate("/");
      });
    }
    refreshAll().catch((e) => console.error("[Dashboard] initial refreshAll failed:", e));
    const iv = setInterval(() => {
      marketApi.watchlist().then(setWatchlist).catch((e) => console.debug("[Dashboard] watchlist poll failed:", e));
      portfolioApi.get().then(setPortfolio).catch((e) => console.debug("[Dashboard] portfolio poll failed:", e));
    }, 45000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = async () => {
    try { await authApi.logout(); } catch (err) { console.debug("[Dashboard] logout API failed (proceeding anyway):", err); }
    navigate("/");
  };

  const onToggleBot = async () => {
    try {
      const res = await settingsApi.toggleBot();
      setSettings((s) => ({ ...s, bot_enabled: res.bot_enabled }));
      toast(res.bot_enabled ? "Auto-trader activated" : "Auto-trader paused");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const onScan = async () => {
    try {
      setScanning(true);
      const res = await settingsApi.scan();
      const executed = res.executed?.length || 0;
      const closed = res.auto_closed?.length || 0;
      toast.success(`Scanned ${res.signals.length} · ${executed} trade${executed === 1 ? "" : "s"} · ${closed} auto-closed for profit/stop`);
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const marketStatus = useMemo(() => {
    const now = new Date();
    const day = now.getUTCDay(); // 0 sun - 6 sat
    const h = now.getUTCHours();
    // NYSE roughly 13:30 - 20:00 UTC weekdays
    const open = day >= 1 && day <= 5 && h >= 13 && h < 20;
    return open ? { label: "Market open", color: "text-emerald-300", dot: "pulse-dot" } : { label: "Market closed", color: "text-amber-300", dot: "pulse-dot-warn" };
  }, []);

  if (!portfolio || !settings) {
    return <div className="min-h-screen grid place-items-center text-slate-400 font-mono text-xs uppercase tracking-[0.3em]">Loading terminal…</div>;
  }

  return (
    <div className="min-h-screen text-slate-100 flex flex-col relative">
      <TradingChartBackground
        className="fixed inset-0 w-full h-full opacity-25 z-0"
        events={chartEvents}
        intense={false}
        symbol={selected}
        price={watchlist?.quotes?.find((q) => q.symbol === selected)?.price ?? null}
      />
      <div className="relative z-10 flex flex-col min-h-screen">
      <RiskDisclaimerModal
        open={risk.open}
        onOpenChange={risk.setOpen}
        onAcknowledge={risk.accept}
        forceAck={!risk.ack}
      />
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#090A0F]/85 border-b border-white/5">
        <div className="px-3 sm:px-4 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/brand/tradingox-logo.png" alt="TradingoX" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)] shrink-0" />
            <div className="font-display text-sm sm:text-base font-bold tracking-tight truncate">TradingoX</div>
            <div className="hidden md:flex items-center gap-2 ml-4 px-3 py-1.5 rounded-full border border-white/10 bg-white/5">
              <span className={marketStatus.dot} />
              <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${marketStatus.color}`}>{marketStatus.label}</span>
            </div>
            <div className="hidden sm:block">
              <BotHealthPill />
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <a href="/faq" data-testid="dashboard-nav-faq" className="hidden md:inline text-xs text-slate-400 hover:text-slate-100 transition">FAQs</a>
            <a href="/contact" data-testid="dashboard-nav-contact" className="hidden md:inline text-xs text-slate-400 hover:text-slate-100 transition">Contact Us</a>
            <div className="hidden sm:block">
              <FounderChip compact />
            </div>
            <div className="hidden md:flex items-center gap-3 pr-3 mr-1 border-r border-white/10">
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Net worth</div>
                <div data-testid={TID.portfolioNetWorth} className="font-mono font-bold text-emerald-300">${portfolio.net_worth.toLocaleString()}</div>
              </div>
            </div>
            <Button data-testid={TID.botScan} onClick={onScan} disabled={scanning}
              className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white h-8 sm:h-9 px-2.5 sm:px-4 text-[11px] sm:text-xs">
              <Radar className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">{scanning ? "Scanning…" : "Scan"}</span>
            </Button>
            <Button data-testid={TID.botToggle} onClick={onToggleBot}
              className={`rounded-full h-8 sm:h-9 px-2.5 sm:px-4 text-[11px] sm:text-xs font-semibold ${settings.bot_enabled ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-white/10 hover:bg-white/20 text-slate-100"}`}>
              {settings.bot_enabled ? <><Pause className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Bot ON</span></> : <><Play className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Bot OFF</span></>}
            </Button>
            <div className="hidden lg:flex items-center gap-2 pl-3 border-l border-white/10">
              {user?.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
              <div className="text-xs text-slate-300 max-w-[120px] truncate">{user?.name || user?.email}</div>
            </div>
            <Button data-testid={TID.headerLogout} onClick={onLogout} variant="ghost"
              className="h-8 w-8 sm:h-9 sm:w-9 p-0 text-slate-400 hover:text-white hover:bg-white/10 rounded-full">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Risk warning bar */}
      <RiskBanner onOpen={() => risk.setOpen(true)} testid="risk-banner-dashboard" />

      {/* Fee reminder */}
      {feeSummary && feeSummary.amount_owed >= (feeSummary.reminder_threshold || 10) && !feeBannerDismissed && (
        <FeeReminderBanner
          owed={feeSummary.amount_owed}
          threshold={feeSummary.reminder_threshold || 10}
          autopayEnabled={feeSummary.autopay_enabled}
          onPay={goToStripeSettle}
          onDismiss={() => setFeeBannerDismissed(true)}
        />
      )}

      {/* Main grid */}
      <main className="px-4 lg:px-8 py-6 max-w-[1600px] mx-auto flex-1 w-full">
        <PortfolioSummary portfolio={portfolio} settings={settings} />

        <div className="grid lg:grid-cols-12 gap-4 mt-4">
          <div className="lg:col-span-3 space-y-4">
            <Watchlist
              watchlist={watchlist}
              onSelect={setSelected}
              selected={selected}
              onChanged={async () => setWatchlist(await marketApi.watchlist())}
            />
          </div>
          <div className="lg:col-span-6 space-y-4">
            <PriceChart symbol={selected} onSymbolChange={setSelected} watchlist={watchlist.symbols} />
            <AISignalPanel symbol={selected} onExecuted={refreshAll} settings={settings} />
          </div>
          <div className="lg:col-span-3 space-y-4">
            <AdminPanel />
            <FeeStatus onReminder={setFeeSummary} />
            <ReferralCard />
            <TradeExecutionPanel symbol={selected} onExecuted={refreshAll} />
            <AlpacaConnect />
            <AutoTraderSettings settings={settings} onSaved={(s) => setSettings(s)} />
          </div>
        </div>

        <div className="mt-4">
          <ProfitAnalytics />
        </div>

        <div className="mt-4">
          <TradeHistory trades={trades} />
        </div>
      </main>

      <RiskFooter />
      <Chatbot user={user} />
      </div>
    </div>
  );
}
